import { z } from 'zod';

export const maxArchiveBytes = 2 * 1024 ** 3;
export const archivePartBytes = 16 * 1024 ** 2;
export const archiveImportStatus = z.enum([
    'pending',
    'uploading',
    'completing',
    'processing',
    'ready',
    'duplicate',
    'cancelling',
    'cancelled',
    'failed',
]);
const publicUrl = z
    .url({ protocol: /^https?$/ })
    .max(4096)
    .refine((value) => {
        const url = new URL(value);
        return (
            !url.username &&
            !url.password &&
            ![...url.searchParams.keys()].some((key) =>
                /^(cfid|cftoken|token|signature|x-amz-.+|access_token)$/i.test(key),
            )
        );
    });
export const archiveImportInput = z.object({
    id: z.uuid(),
    title: z.string().trim().min(1).max(500),
    noticeUrl: publicUrl,
    downloadUrl: publicUrl,
    publishedOn: z.iso.date(),
    fileName: z
        .string()
        .min(1)
        .max(255)
        .refine((value) => /\.zip$/i.test(value)),
    sizeBytes: z.number().int().min(22).max(maxArchiveBytes),
    fileToken: z.string().regex(/^[a-f0-9]{64}$/),
    publicSourceConfirmed: z.literal(true),
});
export type ArchiveImportInput = z.infer<typeof archiveImportInput>;
export const archiveImport = z.object({
    id: z.uuid(),
    title: z.string(),
    noticeUrl: z.string(),
    downloadUrl: z.string(),
    publishedOn: z.iso.date(),
    fileName: z.string(),
    sizeBytes: z.number(),
    fileToken: z.string(),
    status: archiveImportStatus,
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    archiveId: z.uuid().nullable(),
    fingerprint: z.string().nullable(),
    rawSha256: z.string().nullable(),
    fileCount: z.number().int().nullable(),
    expandedBytes: z.number().nullable(),
    errorCode: z.string().nullable(),
});
export type ArchiveImport = z.infer<typeof archiveImport>;
export const archiveStorageStatus = z.object({ available: z.boolean(), maxBytes: z.number(), partBytes: z.number() });
export const archiveTransfer = z.object({
    partBytes: z.number(),
    completedParts: z.array(z.number().int().positive()),
    completedBytes: z.number().nonnegative(),
});
export const archivePartRequest = z.object({
    numbers: z
        .array(
            z
                .number()
                .int()
                .min(1)
                .max(Math.ceil(maxArchiveBytes / archivePartBytes)),
        )
        .min(1)
        .max(8),
});
export const archivePartUrls = z.object({ parts: z.array(z.object({ number: z.number(), url: z.url() })) });
export const archiveFile = z.object({ path: z.string(), sizeBytes: z.number(), sha256: z.string() });
export const archiveFilePage = z.object({ files: z.array(archiveFile), total: z.number().int().nonnegative() });
